import * as React from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { systemClock } from '@reos/config';
import { useLocale, useT } from '../lib/i18n.js';
import { listViewingBookings } from '../lib/viewings-api.js';
import { formatViewingDay, formatViewingTime, groupSlotsByDay } from '../lib/viewing-format.js';
import { errorMessage } from '../lib/properties.js';
import { formatFullDate } from '../lib/relative-time.js';
import { V4Group, V4Row } from '../components/ux-v4.js';
import { PageHead } from '../components/page-head.js';

/**
 * `/prohlidky` (krok CRM4, bod 3) — nadcházející rezervované prohlídky
 * kanceláře po dnech. Do kroku CRM4 stránka PŘIPRAVUJE SE (AUD1/UX1 ji
 * skryly jako prázdnou). Termíny se vypisují a odkazy posílají na kartě
 * nemovitosti — tady je přehled, ne druhé místo práce.
 */
export function ProhlidkyPage(): React.JSX.Element {
  const t = useT();
  const locale = useLocale();
  const [now] = React.useState(() => systemClock.now());
  const bookings = useQuery({
    queryKey: ['viewing-bookings', 'upcoming', now.toDateString()],
    queryFn: () => listViewingBookings({ from: now.toISOString(), status: 'booked' }),
  });
  const items = bookings.data ?? [];
  return (
    <div className="rh-v4-page" data-testid="prohlidky-page">
      <PageHead
        variant="plain"
        breadcrumb={formatFullDate(now)}
        title={t('webAdmin.viewings.pageTitle')}
        subtitle={t('webAdmin.viewings.pageLede')}
      />
      {bookings.isLoading ? (
        <p role="status" className="rh-a2-status">
          {t('webAdmin.viewings.loading')}
        </p>
      ) : null}
      {bookings.error !== null ? (
        <p role="alert" className="rh-a2-error">
          {errorMessage(bookings.error)}
        </p>
      ) : null}
      {bookings.data !== undefined && items.length === 0 ? (
        <div className="rh-a2-empty" data-testid="prohlidky-empty">
          <p>{t('webAdmin.viewings.pageEmpty')}</p>
          <p>
            <Link to="/nemovitosti" data-testid="prohlidky-empty-link">
              {t('webAdmin.viewings.pageEmptyLink')}
            </Link>
          </p>
        </div>
      ) : null}
      {groupSlotsByDay(items).map((group) => (
        <React.Fragment key={group.day}>
          <h2 className="rh-v4-h2">{formatViewingDay(group.items[0]?.startsAt ?? '', locale)}</h2>
          <V4Group testId={`prohlidky-day-${group.day}`}>
            {group.items.map((b) => (
              <V4Row
                key={b.id}
                lead={formatViewingTime(b.startsAt, locale)}
                title={b.propertyLabel ?? t('webAdmin.matches.unnamedProperty')}
                sub={`${b.contactName} · ${b.contactEmail}${b.contactPhone !== null ? ` · ${b.contactPhone}` : ''}`}
                chevron
                render={(className, content) => (
                  <Link to="/nemovitosti/$id" params={{ id: b.propertyId }} className={className} data-testid={`prohlidky-booking-${b.id}`}>
                    {content}
                  </Link>
                )}
              />
            ))}
          </V4Group>
        </React.Fragment>
      ))}
    </div>
  );
}
